import React, { useEffect } from "react"
import { useFormContext } from "react-hook-form"
import styled from "styled-components"
import { Label, Error } from "./input-v2.styled"
import Image from "@src/components/core-image"
import PropTypes from "prop-types"

const getNestedError = (errors, name) => {
  return name.split(".").reduce((acc, key) => acc?.[key], errors)
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`

const IconWrapper = styled.div`
  position: relative;
  left: 14px;
  top: 11px;
  max-height: 0;
  overflow: visible;
  text-align: left;
`

const IconImage = styled(Image)`
  min-height: 20px;
  max-height: 20px;
`

const PostfixWrapper = styled.div`
  position: absolute;
  right: 14px;
  bottom: 12px;

  font-size: 16px;
  color: ${props => props.theme.gray500 || "#999"};
`

const InputStyle = styled.div`
  overflow: hidden;
  width: 100%;

  .form-input {
    width: 100%;
    padding: 14px 10px;
    box-sizing: border-box;
    font-family: system-ui;
    font-size: 16px;
    font-weight: 400;
    border-radius: 8px;
    border-width: 1px;
    height: 42px;
    border: 1px solid #d0d5dd;
    padding-left: ${props => (props.hasPrefix ? "48px" : "14px")};
    padding-right: ${props => (props.hasPostfix ? "48px" : "14px")};

    ::placeholder {
      color: ${props => props.theme.gray500};
    }
  }

  .input-error {
    border-color: red;
    border-radius: 4px;
  }
`

const Prefix = styled.span`
  font-size: 18px;
  color: #999999;
`

const Postfix = styled.span`
  font-size: 16px;
  color: #666;
`

const Input = ({
  label,
  name,
  type = "text",
  validate,
  required,
  defaultValue,
  placeholder,
  className,
  formatter,
  icon = "",
  prefix = "",
  postfix = "",
}) => {
  const {
    register,
    formState: { errors },
    setValue,
  } = useFormContext()

  useEffect(() => {
    setValue(name, defaultValue)
  }, [name, defaultValue, setValue])

  const handleInputChange = event => {
    const value = event.target.value
    const formattedValue = formatter ? formatter(value) : value
    setValue(name, formattedValue)
  }

  return (
    <Wrapper className={className}>
      {label && (
        <Label htmlFor={name} required={required}>
          {label}
        </Label>
      )}

      <InputStyle hasPrefix={icon || prefix} hasPostfix={postfix}>
        {icon && (
          <IconWrapper>
            <IconImage src={icon} alt="icon" />
          </IconWrapper>
        )}
        {prefix && (
          <IconWrapper>
            <Prefix>{prefix}</Prefix>
          </IconWrapper>
        )}

        <input
          className={`form-input ${errors[name] ? "input-error" : ""}`}
          id={name}
          type={type}
          placeholder={placeholder}
          {...register(name, { validate })}
          onChange={handleInputChange}
        />

        {postfix && (
          <PostfixWrapper>
            <Postfix>{postfix}</Postfix>
          </PostfixWrapper>
        )}
      </InputStyle>

      {getNestedError(errors, name)?.message && (
        <Error
          dangerouslySetInnerHTML={{
            __html: getNestedError(errors, name).message,
          }}
        />
      )}
    </Wrapper>
  )
}

export default Input

Input.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  validate: PropTypes.func,
  required: PropTypes.bool,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  className: PropTypes.string,
  formatter: PropTypes.func,
  icon: PropTypes.string,
  prefix: PropTypes.string,
  postfix: PropTypes.string, // New postfix prop
}
